// helpers for constructing the filter expressions as a combination of
// 'and', 'or' of a series of terms

import { BatchModeTransaction } from '@rhybus/api'

import { mpfRListsHttp } from '../api/mpf-r-lists-http'

interface BatchResult {
  Errors: [] | string[]
  result: 'FAILED' | 'PASS'
  results: any
}
function joinTerms(op: string, terms: string[]) {
  const nonBlankTerms = terms.filter((t) => t !== null && t !== '')

  if (nonBlankTerms.length > 0) {
    const combined = nonBlankTerms.join(op)
    return `(${combined})`
  } else {
    return ''
  }
}

function and(...terms: string[]) {
  return joinTerms(' and ', terms)
}

function or(...terms: string[]) {
  return joinTerms(' or ', terms)
}

function isArchivedFilter(includeIsArchived: boolean | null) {
  const filter =
    includeIsArchived === true
      ? `IsArchived eq ${Boolean(true)}`
      : includeIsArchived === false
      ? `IsArchived eq ${Boolean(false)}`
      : ''

  return filter
}

/**
 * Function used throughout the site for getting the blob storage from the Attachments field of a lsit
 *
 * @param {string} listName the name of the list
 * @param {any[]} data the data from the list that needs the expanded Attachments
 * @returns the array of data with an extra column Attachments which is an array of objects.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function getBlobStorage(listName: string, data: any) {
  const list = await mpfRListsHttp().getList(listName)
  const listBlobProvider = list.blobs()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const listBlobInfos: any = await listBlobProvider.listAll()
  const newData = data.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (entry: any) => {
      const matchedAttach = listBlobInfos.find(
        (blobs: { itemKey: string }) => blobs.itemKey === `${listName}__` + entry.eKey
      )
      return {
        ...entry,
        Attachments: matchedAttach ? [matchedAttach] : [],
      }
    }
  )
  return newData
}
const transactionBatch = async (
  batchItems: {
    data: any
    list: string
    op: number
  }[]
): Promise<BatchResult> => {
  let initialBatch // To hold the batch result

  try {
    // Step 1: Execute the batch transaction with the provided items
    const batchResult = await mpfRListsHttp().createBatch(BatchModeTransaction, batchItems)
    initialBatch = await batchResult.execute()

    // Step 2: Check for errors in the batch result
    const errors = initialBatch
      .filter((item) => item.error) // Get items with errors
      .map((item) => item.error) // Extract the error messages

    // If errors are found, set result to "FAILED"
    if (errors.length > 0) {
      console.error('Batch errors:', errors)
      return { Errors: errors, result: 'FAILED', results: initialBatch }
    }

    // Step 3: Return a success result if no errors
    return { Errors: [], result: 'PASS', results: initialBatch }
  } catch (error) {
    console.error('An error occurred while processing the batch:', error)
    return { Errors: [error.message], result: 'FAILED', results: initialBatch || null }
  }
}
const deleteItem = async (listName: string, item: { eKey: string; eTag: string }) => {
  const lists = await mpfRListsHttp().getList(listName)
  await lists.deleteItem(item.eKey, item.eTag)
}
export {
  BatchResult,
  and,
  deleteItem,
  getBlobStorage,
  isArchivedFilter,
  or,
  transactionBatch,
}
